/**
 * Mock data for the 2026-05-04 client batch (Decisions #22–#28):
 * - Theme toggle on auth (state lives in localStorage; no mock data needed)
 * - Package categories (multi-vertical)
 * - Multi-currency + Stripe + PayPal
 * - Device sessions
 * - Signup OTP
 * - End-user 2FA
 * - Platform broadcasts + notifications
 */

import type { LucideIcon } from 'lucide-react';

/* ── Package categories ─────────────────────────────────────────── */

export type PackageCategoryIconKey =
  | 'shopping-cart'
  | 'graduation-cap'
  | 'wifi'
  | 'briefcase'
  | 'building'
  | 'truck';

export type PackageCategory = {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  iconKey: PackageCategoryIconKey;
  /** true = signup triggers Laravel POS DB provisioning; false = billing-only vertical */
  provisionsPos: boolean;
  isActive: boolean;
  sortOrder: number;
  packageCount: number;
};

export const packageCategories: PackageCategory[] = [
  {
    id: 'cat_pos',
    slug: 'pos',
    displayName: 'POS Package',
    description: 'Bangladesh-first POS for retail and restaurants. Includes Laravel POS access.',
    iconKey: 'shopping-cart',
    provisionsPos: true,
    isActive: true,
    sortOrder: 10,
    packageCount: 7,
  },
  {
    id: 'cat_school',
    slug: 'school',
    displayName: 'School',
    description: 'Subscription billing for schools, colleges, and coaching centers. Billing-only.',
    iconKey: 'graduation-cap',
    provisionsPos: false,
    isActive: true,
    sortOrder: 20,
    packageCount: 3,
  },
  {
    id: 'cat_internet',
    slug: 'internet',
    displayName: 'Internet',
    description: 'ISP / mobile-data subscription billing. Billing-only.',
    iconKey: 'wifi',
    provisionsPos: false,
    isActive: true,
    sortOrder: 30,
    packageCount: 2,
  },
];

/* ── Currencies ─────────────────────────────────────────────────── */

export type CurrencyCode = 'BDT' | 'USD' | 'GBP' | 'EUR';

export type Currency = {
  code: CurrencyCode;
  symbol: string;
  name: string;
  decimals: number;
  flag: string; // emoji flag for region
  defaultCountry: string; // ISO 3166-1 alpha-2
  isActive: boolean;
  sortOrder: number;
};

export const currencies: Currency[] = [
  {
    code: 'BDT',
    symbol: '৳',
    name: 'Bangladeshi Taka',
    decimals: 2,
    flag: '🇧🇩',
    defaultCountry: 'BD',
    isActive: true,
    sortOrder: 0,
  },
  {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    decimals: 2,
    flag: '🇺🇸',
    defaultCountry: 'US',
    isActive: true,
    sortOrder: 10,
  },
  {
    code: 'GBP',
    symbol: '£',
    name: 'Pound Sterling',
    decimals: 2,
    flag: '🇬🇧',
    defaultCountry: 'GB',
    isActive: true,
    sortOrder: 20,
  },
  {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    decimals: 2,
    flag: '🇪🇺',
    defaultCountry: 'DE',
    isActive: true,
    sortOrder: 30,
  },
];

/* ── Payment gateways (extended) ─────────────────────────────────── */

export type GatewaySlug = 'sslcommerz' | 'stripe' | 'paypal' | 'manual_cash';

export type GatewayMode = 'live' | 'sandbox' | 'disabled';

export type PaymentGatewayDetailed = {
  slug: GatewaySlug;
  displayName: string;
  description: string;
  supportedCurrencies: CurrencyCode[];
  mode: GatewayMode;
  isPrimaryFor?: CurrencyCode[];
  lastChargeAt: string | null;
  lastChargeAmount: number | null;
  lastChargeCurrency: CurrencyCode | null;
  totalChargesThisMonth: number; // count
  totalAmountThisMonth: number; // sum in the gateway's primary currency
};

export const paymentGatewaysDetailed: PaymentGatewayDetailed[] = [
  {
    slug: 'sslcommerz',
    displayName: 'SSLCommerz',
    description: 'Bangladesh-first gateway. Cards, bKash, Nagad, bank transfers.',
    supportedCurrencies: ['BDT'],
    mode: 'live',
    isPrimaryFor: ['BDT'],
    lastChargeAt: '2026-05-03T14:00:00Z',
    lastChargeAmount: 1500,
    lastChargeCurrency: 'BDT',
    totalChargesThisMonth: 142,
    totalAmountThisMonth: 187400,
  },
  {
    slug: 'stripe',
    displayName: 'Stripe',
    description: 'International cards. Recommended for USD / GBP / EUR markets.',
    supportedCurrencies: ['USD', 'GBP', 'EUR'],
    mode: 'live',
    isPrimaryFor: ['USD', 'GBP', 'EUR'],
    lastChargeAt: '2026-05-02T08:14:00Z',
    lastChargeAmount: 19,
    lastChargeCurrency: 'USD',
    totalChargesThisMonth: 28,
    totalAmountThisMonth: 612,
  },
  {
    slug: 'paypal',
    displayName: 'PayPal',
    description: 'Fallback for USD / GBP / EUR. Customer login + approve.',
    supportedCurrencies: ['USD', 'GBP', 'EUR'],
    mode: 'sandbox',
    lastChargeAt: null,
    lastChargeAmount: null,
    lastChargeCurrency: null,
    totalChargesThisMonth: 0,
    totalAmountThisMonth: 0,
  },
  {
    slug: 'manual_cash',
    displayName: 'Cash (manual)',
    description: 'Platform admin records cash payments via /admin/cash-entry.',
    supportedCurrencies: ['BDT'],
    mode: 'live',
    lastChargeAt: '2026-05-03T13:51:00Z',
    lastChargeAmount: 300,
    lastChargeCurrency: 'BDT',
    totalChargesThisMonth: 38,
    totalAmountThisMonth: 22400,
  },
];

/** Helper: which gateways accept a given currency. */
export function gatewaysForCurrency(currency: CurrencyCode): PaymentGatewayDetailed[] {
  return paymentGatewaysDetailed.filter(
    (g) => g.mode !== 'disabled' && g.supportedCurrencies.includes(currency),
  );
}

/* ── Device sessions ─────────────────────────────────────────────── */

export type SessionRevokedReason =
  | 'user_signed_out'
  | 'cap_exceeded'
  | 'admin_revoked'
  | 'password_changed'
  | 'mfa_changed'
  | 'expired'
  | null;

export type DeviceSession = {
  id: string;
  deviceLabel: string;
  deviceIcon: 'monitor' | 'smartphone' | 'tablet' | 'laptop';
  userAgent: string;
  ip: string;
  geoCity: string;
  geoCountry: string;
  createdAt: string;
  lastActiveAt: string; // human-readable for the mock
  expiresAt: string;
  isCurrent: boolean;
  revokedAt: string | null;
  revokedReason: SessionRevokedReason;
};

/** "Current logged-in user's" sessions, displayed at /profile Sessions tab. */
export const myActiveSessions: DeviceSession[] = [
  {
    id: 'ses_1',
    deviceLabel: 'Mac · Chrome 126',
    deviceIcon: 'laptop',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 14_4) Chrome/126.0.0.0',
    ip: '103.108.135.12',
    geoCity: 'Sylhet',
    geoCountry: 'BD',
    createdAt: '2026-04-28T09:14:00Z',
    lastActiveAt: 'now (this device)',
    expiresAt: '2026-05-05T09:14:00Z',
    isCurrent: true,
    revokedAt: null,
    revokedReason: null,
  },
  {
    id: 'ses_2',
    deviceLabel: 'iPhone · Safari',
    deviceIcon: 'smartphone',
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_5) Safari/17.5',
    ip: '103.108.140.41',
    geoCity: 'Sylhet',
    geoCountry: 'BD',
    createdAt: '2026-05-03T16:02:00Z',
    lastActiveAt: '4 hours ago',
    expiresAt: '2026-05-10T16:02:00Z',
    isCurrent: false,
    revokedAt: null,
    revokedReason: null,
  },
  {
    id: 'ses_3',
    deviceLabel: 'Windows · Edge',
    deviceIcon: 'monitor',
    userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Edge/124.0.0.0',
    ip: '103.108.150.78',
    geoCity: 'Dhaka',
    geoCountry: 'BD',
    createdAt: '2026-05-01T10:00:00Z',
    lastActiveAt: '3 days ago',
    expiresAt: '2026-05-08T10:00:00Z',
    isCurrent: false,
    revokedAt: null,
    revokedReason: null,
  },
];

export const SESSION_CAP_DEFAULT = 5;
export const SESSION_CAP_MAX = 20;
export const SESSION_CAP_MIN = 1;

/* ── 2FA factors ─────────────────────────────────────────────────── */

export type MfaKind = 'sms' | 'email' | 'totp_authy' | 'totp_google';

export type MfaFactor = {
  id: string;
  kind: MfaKind;
  label: string;
  phoneOrEmail?: string;
  isPrimary: boolean;
  verifiedAt: string;
  lastUsedAt: string;
};

/** "Current logged-in user's" 2FA state, displayed at /profile Security tab. Default: empty (2FA off). */
export const my2faFactors: MfaFactor[] = [];

/**
 * Demo flag — set to true to simulate "the current user has 2FA enabled".
 * When true, Login.tsx routes to /auth/mfa-challenge instead of /.
 * Toggle via the demo control in /login during the demo, or change the default.
 */
export const MOCK_CURRENT_USER_HAS_2FA = false;

/** Mock primary 2FA factor for the challenge page. */
export const mockPrimary2faFactor = {
  kind: 'totp_authy' as const,
  label: 'Authy',
  phoneOrEmailMasked: '·····@khan-bakery.com',
};

/** Demo: a 6-digit TOTP that always succeeds in mock mode. */
export const MOCK_VALID_TOTP = '123456';

/** When a user enables 2FA, 10 single-use recovery codes are generated. */
export const my2faRecoveryCodes: string[] = [
  '7f3a-9b1c-2d5e',
  'a1b2-c3d4-e5f6',
  '0p9o-8i7u-6y5t',
  'q2w3-e4r5-t6y7',
  '1z2x-3c4v-5b6n',
  'h7j8-k9l0-m1n2',
  'd5f6-g7h8-j9k0',
  '4r3e-2w1q-9p8o',
  's5d6-f7g8-h9j0',
  'b3n4-m5x6-c7v8',
];

/* ── Signup OTP state (mock) ─────────────────────────────────────── */

export type OtpState =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent'; sentAt: string; expiresAt: string; resendAvailableAt: string }
  | { state: 'verifying' }
  | { state: 'verified' }
  | { state: 'failed'; attemptsLeft: number; message: string }
  | { state: 'expired' }
  | { state: 'locked'; message: string };

/** For mock: any code that's exactly "1234" is accepted; anything else fails. */
export const MOCK_VALID_OTP = '1234';

/* ── Notifications & Broadcasts ──────────────────────────────────── */

export type NotificationCategory =
  | 'billing'
  | 'security'
  | 'system'
  | 'team'
  | 'commission'
  | 'application';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export type AppNotification = {
  id: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  body: string;
  actionUrl?: string;
  actionLabel?: string;
  createdAt: string; // human-readable for the mock
  readAt: string | null;
  dismissedAt: string | null;
  pinnedUntil: string | null; // ISO; if > now, eligible for modal/banner
  broadcastId?: string;
};

/** "Current user's" notification feed. Mix of unread + read. */
export const myNotifications: AppNotification[] = [
  {
    id: 'ntf_1',
    category: 'billing',
    severity: 'warning',
    title: 'Invoice INV-1042 due in 2 days',
    body: '৳1,500 for Pro plan. Pay before 06 May 2026 to avoid suspension.',
    actionUrl: '/billing/INV-1042',
    actionLabel: 'Pay now',
    createdAt: '2 hours ago',
    readAt: null,
    dismissedAt: null,
    pinnedUntil: null,
  },
  {
    id: 'ntf_2',
    category: 'security',
    severity: 'warning',
    title: 'New device signed in',
    body: 'iPhone · Safari from Sylhet, BD. If this wasn\'t you, revoke now.',
    actionUrl: '/profile?tab=sessions',
    actionLabel: 'Review devices',
    createdAt: 'yesterday',
    readAt: null,
    dismissedAt: null,
    pinnedUntil: null,
  },
  {
    id: 'ntf_3',
    category: 'system',
    severity: 'warning',
    title: 'Maintenance window upcoming',
    body: 'Sun 05 May, 04:00–06:00 BD. Brief unavailability expected during database upgrade.',
    actionUrl: '/help',
    actionLabel: 'Status page',
    createdAt: '3 days ago',
    readAt: null,
    dismissedAt: null,
    pinnedUntil: '2026-05-05T06:00:00Z',
    broadcastId: 'bc_2',
  },
  {
    id: 'ntf_4',
    category: 'billing',
    severity: 'info',
    title: 'Payment received — ৳1,500',
    body: 'Invoice INV-1041 paid via SSLCommerz. Subscription extended to 03 Jun 2026.',
    createdAt: '03 May',
    readAt: '03 May, 14:32',
    dismissedAt: null,
    pinnedUntil: null,
  },
  {
    id: 'ntf_5',
    category: 'team',
    severity: 'info',
    title: 'Team invite accepted',
    body: 'Rina Begum joined Khan Bakery as a cashier.',
    actionUrl: '/team',
    createdAt: '02 May',
    readAt: '02 May, 11:00',
    dismissedAt: null,
    pinnedUntil: null,
  },
];

export type BroadcastAudienceKind =
  | 'all_resellers'
  | 'all_end_customers'
  | 'reseller_customers'
  | 'one_business';

export type Broadcast = {
  id: string;
  title: string;
  body: string;
  audienceKind: BroadcastAudienceKind;
  audienceTargetId?: string; // reseller_id or business_id
  audienceTargetLabel?: string;
  severity: NotificationSeverity;
  deliveryChannels: Array<'notification_panel' | 'login_modal' | 'top_banner'>;
  linkUrl?: string;
  scheduledAt: string;
  sentAt: string | null;
  recipientCount: number;
  readCount: number;
  pinnedUntil: string | null;
  createdByEmail: string;
  createdAt: string;
};

export const broadcasts: Broadcast[] = [
  {
    id: 'bc_1',
    title: 'Acme price floor update',
    body: 'Heads up — the Pro tier minimum is rising to ৳1,200/mo on 01 Jun 2026. Existing packages are grandfathered until you next edit them.',
    audienceKind: 'all_resellers',
    severity: 'info',
    deliveryChannels: ['notification_panel'],
    scheduledAt: '2026-05-01T09:00:00Z',
    sentAt: '2026-05-01T09:00:14Z',
    recipientCount: 5,
    readCount: 4,
    pinnedUntil: null,
    createdByEmail: 'sayem@itcitysoft.com',
    createdAt: '2026-05-01T08:55:00Z',
  },
  {
    id: 'bc_2',
    title: 'Maintenance window — 04:00 Sun 05 May',
    body: "We'll be running database upgrades from 04:00 to 06:00 BD time on Sun 05 May. Brief unavailability expected. Status updates will be posted in your dashboard.",
    audienceKind: 'all_end_customers',
    severity: 'warning',
    deliveryChannels: ['notification_panel', 'login_modal', 'top_banner'],
    linkUrl: '/help',
    scheduledAt: '2026-05-03T08:00:00Z',
    sentAt: '2026-05-03T08:00:08Z',
    recipientCount: 412,
    readCount: 247,
    pinnedUntil: '2026-05-05T06:00:00Z',
    createdByEmail: 'sayem@itcitysoft.com',
    createdAt: '2026-05-03T07:50:00Z',
  },
  {
    id: 'bc_3',
    title: 'Khan Bakery refund processed',
    body: 'Your refund of ৳1,500 has been processed and will reflect in your bKash account within 24 hours.',
    audienceKind: 'one_business',
    audienceTargetId: 'biz_42',
    audienceTargetLabel: 'Khan Bakery',
    severity: 'info',
    deliveryChannels: ['notification_panel'],
    scheduledAt: '2026-05-06T10:00:00Z',
    sentAt: null,
    recipientCount: 1,
    readCount: 0,
    pinnedUntil: null,
    createdByEmail: 'sayem@itcitysoft.com',
    createdAt: '2026-05-04T18:00:00Z',
  },
];

/** Helper: unread broadcasts that should pop the after-login modal. */
export function pendingModalBroadcasts(): AppNotification[] {
  const nowIso = new Date('2026-05-04T12:00:00Z').toISOString();
  return myNotifications.filter(
    (n) =>
      !n.readAt &&
      !n.dismissedAt &&
      n.pinnedUntil &&
      n.pinnedUntil > nowIso &&
      (n.severity === 'warning' || n.severity === 'critical'),
  );
}

/* ── Platform settings additions ─────────────────────────────────── */

export const platformBatchSettings = {
  maxActiveSessionsDefault: SESSION_CAP_DEFAULT,
  signupOtpRequired: true,
  signupOtpDeliveryDefault: 'both' as 'sms' | 'email' | 'both',
  resellerTwoFaRequiredDefault: false,
  platformAdminTwoFaRequired: true,
  activeCurrencies: ['BDT', 'USD', 'GBP', 'EUR'] as CurrencyCode[],
  defaultCurrency: 'BDT' as CurrencyCode,
};
